/* eslint-disable @typescript-eslint/naming-convention */
import type { DocumentTemplateScope, ReadyDocumentTemplatePart } from "./documentTemplates";
import type { UserApplication } from "./user-application";

import type { MessageStatus, Step, Team, TemplateLabel, User, WebMode } from "./index";

export type ApplicationType = "document" | "prompt" | "brain";

export const appIconNames = [
	"callAnalysisApp",
	"documentAnalysisApp",
	"documentValidationApp",
	"dueDiligenceApp",
	"genericApp",
	"memoDraftingApp",
	"proposalApp",
	"questionnairesApp",
	"researchApp",
	"benchmarkApp",
	"goNoGoApp",
] as const;

export type AppIconNames = (typeof appIconNames)[number];

export enum TimeSavedEnum {
	M15_30 = "M15_30",
	H1 = "H1",
	HALF_DAY = "HALF_DAY",
	FULL_DAY = "FULL_DAY",
	MULTI_DAYS = "MULTI_DAYS",
	WEEK_1 = "WEEK_1",
}

export type ApplicationRightsType = "company" | "shared" | "public" | undefined;

export type AppFilters = "all" | "my-apps" | "shared-apps" | "templates";

export interface Application {
	id: string;
	name: string;
	description?: string;
	type: ApplicationType;
	createdAt: Date;
	updatedAt: Date;
	lastUpdatedAt: Date;
	rights?:
		| {
				readOnly: boolean;
				type?: ApplicationRightsType;
		  }
		| undefined;
	icon: AppIconNames;
	usersApplicationsCount?: number | undefined;
	isTemplate: boolean;
	isPublic: boolean;
	isLive: boolean;
	templateLabels?: TemplateLabel[] | undefined;
	ownerId?: string | undefined;
	owner?: User | undefined;
	blocks?: Block[] | undefined;
	sessions?: Session[] | undefined;
	steps?: Step[] | undefined;
	timeSaved?: TimeSavedEnum | undefined;
	medianExecutionTime: number;
	userApplications?: UserApplication[] | undefined;
	teams?: Team[] | undefined;
}

export interface Session {
	id: string;
	createdAt: Date;
	updatedAt: Date;
	lastMessageAt: Date;
	title?: string | undefined;
	applicationId: string;
	userId: string;
	blockStatuses?: Record<string, MessageStatus>;
}

export type SessionWithApplication = Session & {
	application: Pick<Application, "id" | "name">;
};

export interface SessionWithCost extends Session {
	cost: number;
}

export type SessionWithCostAppUser = SessionWithCost & {
	application: Application;
	user: User;
};

export interface BlockParent {
	id: string;
	blockId: string;
	parentId: string;
	parents?: BlockParent[] | undefined;
}

export interface Block {
	id: string;
	title: string;
	index: number;
	isHidden: boolean;
	query: string;
	applicationId: string;
	updatedAt: Date;
	pauseInstruction?: string | undefined;
	knowledges?: BlockKnowledge[] | undefined;
	systemPrompt?: string | undefined;
	webMode: WebMode;
	isActive?: boolean | undefined;
	parents?: BlockParent[] | undefined;
	documentTemplateParts?: BlockDocumentTemplatePart[] | undefined;
	needsUpdateAgentMode?: NeedsUpdateAgentMode | undefined;
}

export const needsUpdateAgentMode = ["disabled", "auto", "forced"] as const;

export type NeedsUpdateAgentMode = (typeof needsUpdateAgentMode)[number];

export interface BlockDocumentTemplatePart {
	id: string;
	blockId: string;
	documentTemplatePartId: string;
	documentTemplate: { id: string; title: string; type: DocumentTemplateScope };
	createdAt: string;
	strict?: boolean;
	documentTemplatePart: ReadyDocumentTemplatePart;
}

export interface BlockKnowledge {
	id: string;
	blockId: string;
	knowledgeBlockId?: string | undefined;
	knowledgeStepId?: string | undefined;
	knowledgeBrainId?: string | undefined;
	createdAt: Date;
	updatedAt: Date;
}

export interface ApplicationNameWithFirstBlockUrl {
	applicationName: string;
	firstBlockUrl: string;
}
